import {
  type CellData,
  type RowData,
  type Column,
  type columnResizingState,
  type ColumnSizingState,
  type ColumnPinningPosition,
} from '@tanstack/react-table';
import { column_getIsFirstColumn } from '@tanstack/react-table/static-functions';

import { devLog } from '../../../../core/utils/logging.js';
import {
  DATA_TABLE_CELL_VERTICAL_PADDING_MAP,
  DATA_TABLE_DEFAULT_ICON_PADDING,
  DATA_TABLE_DEFAULT_ICON_SIZE,
  DATA_TABLE_DEFAULT_MIN_COLUMN_WIDTH,
  DATA_TABLE_STRATO_RESIZING_SIZE_CSS_VAR,
  DATA_TABLE_THRESHOLD_PILL_SIZE,
} from '../../constants.js';
import { toPhysicalPinnedPosition } from '../ColumnPinning/column-pinning-utils.js';
import { isRowSelectionColumnDef } from '../RowSelection/RowSelection.js';
import type { DataTableThresholdsNormalizedRule } from '../Thresholds/threshold-types.js';
import type { TDataTableFeatures } from '../types/features.types.js';
import type {
  DataTableColumnGridTemplates,
  GetMeasuredColumnWidthFn,
} from './layout-types.js';
import { isFractionWidth, isWidthExtendedConfig } from './width-utils.js';

/**
 * Gets the width for a prefix column based on its position and type.
 *
 * @param column - The prefix column to process
 * @param rowDensity - The row density configured for the table
 * @param rowThresholds - The row thresholds configured for the table
 * @returns Width in pixels
 * @internal
 */
export function getPrefixColumnWidth<
  TData extends RowData,
  TValue extends CellData,
>(
  column: Column<TDataTableFeatures, TData, TValue>,
  rowDensity: 'comfortable' | 'condensed' | 'default',
  rowThresholds?: DataTableThresholdsNormalizedRule<TData>[],
): number {
  const isFirstColumn = column_getIsFirstColumn(column, 'start');

  // icons in built-in columns have padding left and right
  const iconPadding = isRowSelectionColumnDef(column.columnDef)
    ? 0
    : 2 * DATA_TABLE_DEFAULT_ICON_PADDING;

  let rowDensityPadding = DATA_TABLE_CELL_VERTICAL_PADDING_MAP[rowDensity];
  let thresholdPillSize = 0;

  if (isFirstColumn) {
    rowDensityPadding *= 2;
    const hasThresholdPill = rowThresholds?.some(
      (threshold) => threshold.type === 'pill',
    );
    thresholdPillSize = hasThresholdPill ? DATA_TABLE_THRESHOLD_PILL_SIZE : 0;
  }

  return (
    DATA_TABLE_DEFAULT_ICON_SIZE +
    iconPadding +
    thresholdPillSize +
    rowDensityPadding
  );
}

/**
 * Gets the target template array based on the pinning state of the column.
 *
 * @param pinnedState - Pinning state of the column
 * @param templates - Object containing left, center, and right templates
 * @returns The target template array
 * @internal
 */
export function getTargetTemplate(
  pinnedState: ColumnPinningPosition,
  templates: DataTableColumnGridTemplates,
): string[] {
  const physicalPosition = toPhysicalPinnedPosition(pinnedState);
  if (!physicalPosition) {
    return templates.center;
  }
  return templates[physicalPosition];
}

/**
 * Clamps a width value between min and max bounds.
 *
 * @param width - The width to clamp
 * @param minWidth - Minimum width
 * @param maxWidth - Maximum width
 * @returns Clamped width in pixels
 */
function clampWidthToPx(
  width: number,
  minWidth: number,
  maxWidth: number,
): string {
  return `${Math.min(Math.max(width, minWidth), maxWidth)}px`;
}

/**
 * Calculates the width for a column with a fixed-width parent by distributing the
 * remaining space among the child columns without defined widths.
 *
 * @param column - The column to process
 * @param parentWidth - The fixed width of the parent column
 * @param getMeasuredWidth - Function to get the measured width of a column
 * @returns Calculated width in pixels
 */
function calculateParentDependentWidth<
  TData extends RowData,
  TValue extends CellData,
>(
  column: Column<TDataTableFeatures, TData, TValue>,
  parentWidth: number,
  getMeasuredWidth: GetMeasuredColumnWidthFn,
): string {
  let totalDefinedChildWidths = 0;
  let undefinedWidthChildCount = 1;

  column.parent?.getVisibleLeafColumns().forEach((childCol) => {
    const childWidth = childCol.columnDef.width;
    if (childWidth === undefined && childCol.id !== column.id) {
      undefinedWidthChildCount++;
    } else if (typeof childWidth === 'number') {
      totalDefinedChildWidths += childWidth;
    } else if (childWidth === 'auto' || childWidth === 'content') {
      totalDefinedChildWidths += getMeasuredWidth(childCol.id) ?? 0;
    } else if (isFractionWidth(childWidth)) {
      devLog(
        'Fraction width is not allowed in combination with a fixed width parent column.',
      );
    }
  });

  const remainingWidth =
    (parentWidth - totalDefinedChildWidths) / undefinedWidthChildCount;
  return `${remainingWidth}px`;
}

/**
 * Get the template value for a column with a fraction width configuration.
 * If the parent column also has a fraction width defined, the child's fraction is relative to the parent's fraction.
 *
 * @param parent - The parent column
 * @param width - The fraction width configuration
 * @param minWidth - Minimum width of the current column
 * @returns Calculated fraction width
 */
function getFractionWidthTemplateValue<
  TData extends RowData,
  TValue extends CellData,
>(
  parent: Column<TDataTableFeatures, TData, TValue> | undefined,
  width: `${number}fr`,
  minWidth: number,
): string {
  if (!parent || !isFractionWidth(parent.columnDef.width)) {
    return `minmax(${minWidth}px, ${width})`;
  }

  const parentFraction = Number.parseFloat(parent.columnDef.width);
  const columnFraction = Number.parseFloat(width);
  const visibleSiblingColumns = parent.getVisibleLeafColumns();
  const siblingColumnsSum = visibleSiblingColumns.reduce((sum, col) => {
    return isFractionWidth(col.columnDef.width)
      ? sum + Number.parseFloat(col.columnDef.width)
      : sum;
  }, 0);

  const calculatedFraction =
    parentFraction * (columnFraction / siblingColumnsSum);
  return `${calculatedFraction}fr`;
}

/**
 * Gets the template value for 'auto' or 'content' width configurations.
 * Returns the measured width if available, or the CSS grid template value
 * to stretch the column depending on the configuration.
 *
 * @param measuredWidth - The measured width of the column, if available
 * @param isContent - Whether the width type is 'content'
 * @returns The CSS grid template value for the column
 */
function getContentOrAutoWidthTemplateValue(
  measuredWidth: number | undefined,
  isContent: boolean,
): string {
  if (measuredWidth !== undefined) {
    return `${measuredWidth}px`;
  }
  return isContent ? 'max-content' : 'minmax(min-content, auto)';
}

/**
 * Gets the template value for string or extended config width types.
 *
 * @param column - The column to process
 * @param width - The width configuration
 * @param minWidth - Minimum width of the column
 * @param getMeasuredWidth - Function to get the measured width of a column
 * @returns The CSS grid template value for the column
 */
function getStringWidthTemplateValue<
  TData extends RowData,
  TValue extends CellData,
>(
  column: Column<TDataTableFeatures, TData, TValue>,
  width:
    | `${number}fr`
    | 'auto'
    | 'content'
    | { type: 'auto' | 'content'; maxWidth?: number },
  minWidth: number,
  getMeasuredWidth: GetMeasuredColumnWidthFn,
): string {
  if (isFractionWidth(width)) {
    return getFractionWidthTemplateValue(column.parent, width, minWidth);
  }
  // we need to check the extended config separately to narrow down the type
  if (isWidthExtendedConfig(width)) {
    return getContentOrAutoWidthTemplateValue(
      getMeasuredWidth(column.id),
      width.type === 'content',
    );
  }
  return getContentOrAutoWidthTemplateValue(
    getMeasuredWidth(column.id),
    width === 'content',
  );
}

/**
 * Gets the fallback width for columns without a configured width.
 *
 * @param column - The column to process
 * @param minWidth - Minimum width of the column
 * @param getMeasuredWidth - Function to get the measured width of a column
 * @returns The CSS grid template value for the column
 */
function getUndefinedWidthTemplateValue<
  TData extends RowData,
  TValue extends CellData,
>(
  column: Column<TDataTableFeatures, TData, TValue>,
  minWidth: number,
  getMeasuredWidth: GetMeasuredColumnWidthFn,
): string {
  const parentWidth = column.parent?.columnDef.width;

  // Consider the parent's width if it is defined
  if (column.parent && typeof parentWidth === 'number') {
    return calculateParentDependentWidth(column, parentWidth, getMeasuredWidth);
  }

  // Fall back to 1fr if no width is defined, and then use the measured width once available
  const measuredWidth = getMeasuredWidth(column.id, true);
  return measuredWidth === undefined
    ? `minmax(${minWidth}px, 1fr)`
    : `${measuredWidth}px`;
}

/**
 * If the provided column is a builtin column (prefix, suffix, or spacer),
 * its grid template value is added to the appropriate column grid template.
 *
 * Mutates the provided `columnGridTemplates` as a side effect.
 *
 * @param column - The column to process
 * @param rowDensity - The row density configured for the table
 * @param rowThresholds - Row thresholds configured for the table
 * @param columnGridTemplates - Object containing left, center, and right templates
 * @returns True if the column is a builtin column and was handled, false otherwise
 * @internal
 */
export function processBuiltinColumn<
  TData extends RowData,
  TValue extends CellData,
>(
  column: Column<TDataTableFeatures, TData, TValue>,
  rowDensity: 'comfortable' | 'condensed' | 'default',
  rowThresholds: DataTableThresholdsNormalizedRule<TData>[] | undefined,
  columnGridTemplates: DataTableColumnGridTemplates,
): boolean {
  if (column.columnDef.meta?.builtinPlacement === 'prefix') {
    columnGridTemplates.left.push(
      `${getPrefixColumnWidth(column, rowDensity, rowThresholds)}px`,
    );
    return true;
  }

  if (column.columnDef.meta?.spacer === true) {
    columnGridTemplates.center.push('auto');
    return true;
  }

  if (column.columnDef.meta?.builtinPlacement === 'suffix') {
    columnGridTemplates.right.push('min-content');
    return true;
  }

  return false;
}

/**
 * Gets the grid template column entry for a column based on its width configuration.
 *
 * @param column - The column to process
 * @param columnSizing - Current column sizing state
 * @param columnSizingInfo - Current column sizing info
 * @param getMeasuredWidth - Function to get the measured width of a column
 * @returns The grid template string for this column, or undefined
 * @internal
 */
export function getColumnTemplateEntry<
  TData extends RowData,
  TValue extends CellData,
>(
  column: Column<TDataTableFeatures, TData, TValue>,
  columnSizing: ColumnSizingState,
  columnSizingInfo: columnResizingState,
  getMeasuredWidth: GetMeasuredColumnWidthFn,
): string | undefined {
  const {
    minWidth = DATA_TABLE_DEFAULT_MIN_COLUMN_WIDTH,
    width,
    maxWidth = Number.MAX_SAFE_INTEGER,
  } = column.columnDef;

  // Handle columns that are currently being resized
  if (columnSizingInfo.isResizingColumn === column.id) {
    return `var(${DATA_TABLE_STRATO_RESIZING_SIZE_CSS_VAR})`;
  }

  // Resized columns have a specific column size defined
  if (columnSizing[column.id] !== undefined) {
    return clampWidthToPx(columnSizing[column.id], minWidth, maxWidth);
  }

  // If no width is defined, we define a layout fallback
  if (width === undefined) {
    return getUndefinedWidthTemplateValue(column, minWidth, getMeasuredWidth);
  }

  // If the width is a number, treat it as a pixel value
  if (typeof width === 'number') {
    return clampWidthToPx(width, minWidth, maxWidth);
  }

  // Handle string and width extended configs
  if (typeof width === 'string' || isWidthExtendedConfig(width)) {
    return getStringWidthTemplateValue(
      column,
      width,
      minWidth,
      getMeasuredWidth,
    );
  }

  return undefined;
}
